import { useUserContext } from '@/hooks/useUserContext';
import { getDevRoleOverride } from './dev-role-override';

/** Spellings of the view-only role seen across `/usercontext` ("ViewOnly") and the demo user context. */
const VIEW_ONLY_ROLES = new Set(['viewonly', 'view_only', 'view only']);

/**
 * Returns whether the current user may change business data.
 *
 * The backend answers `403` to every write under `/api/v1` for a ViewOnly user (`BusinessWritePolicy`),
 * so write controls are pointless for that role. This hook mirrors that policy in the UI.
 *
 * Fails **open** on purpose: only a role that reads as view-only loses write controls. An unknown,
 * missing or still-loading role keeps them, so Administrator and User are unaffected even when
 * `/usercontext` is slow or its role is spelled differently than expected — the server, not the UI,
 * is the boundary.
 *
 * The dev-only `demo.role` override documented on `useIsAdmin` applies here too: setting it to
 * `view_only` simulates a view-only user, `user` and `administrator` simulate a writer.
 */
export function useCanWrite(): { canWrite: boolean; isLoading: boolean } {
  const { data, isLoading } = useUserContext();

  const role = data?.role?.trim().toLowerCase();
  const realCanWrite = role === undefined || !VIEW_ONLY_ROLES.has(role);

  const override = getDevRoleOverride();
  if (override !== null) {
    if (override === 'view_only') {
      return { canWrite: false, isLoading };
    }
    if (override === 'user' || override === 'administrator') {
      return { canWrite: true, isLoading };
    }
  }

  return { canWrite: realCanWrite, isLoading };
}
